import React, { useState } from 'react';
import { Button, Form, Input, Message, Modal, SelectPicker } from 'rsuite';
import { openSuccessToaster } from 'services/toast.service';
import {
  updateBagelDocument,
  UpdateBagelDocumentParams,
} from '../../../api/bagel-documents';
import { BagelDocument, BagelDocumentCategory } from '../../../types';
import type { BagelDocumentMetadata } from '../../../types';
import AutoGrowTextarea from '../AutoGrowTextarea';
import {
  CATEGORY_OPTIONS,
  CHUNK_STRATEGY_OPTIONS,
} from '../../constants/bagelDocumentFormOptions';

export interface BagelDocumentEditMetadataModalProps {
  doc: BagelDocument;
  onClose: () => void;
  onUpdated: (doc: BagelDocument) => void;
  onReprocess: (id: string) => Promise<void>;
}

export default function BagelDocumentEditMetadataModal({
  doc,
  onClose,
  onUpdated,
  onReprocess,
}: BagelDocumentEditMetadataModalProps) {
  const isScla = doc.domain === 'scla';
  const [name, setName] = useState(doc.name);
  const [title, setTitle] = useState(doc.metadata?.title ?? doc.name);
  const [category, setCategory] = useState<BagelDocumentCategory>(
    doc.category ?? 'urban_txt',
  );
  const [description, setDescription] = useState(doc.description ?? '');
  const [docType, setDocType] = useState(doc.metadata?.docType ?? '');
  const [tags, setTags] = useState((doc.metadata?.tags ?? []).join(', '));
  const [chunkStrategy, setChunkStrategy] = useState<
    NonNullable<BagelDocumentMetadata['chunkStrategy']>
  >(doc.metadata?.chunkStrategy ?? 'by_paragraph');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const buildPayload = (): UpdateBagelDocumentParams => {
    const tagList = tags
      .split(',')
      .map(tag => tag.trim())
      .filter(Boolean);

    if (isScla) {
      return {
        title: title.trim(),
        doc_type: docType.trim() || undefined,
        tags: tagList,
        chunk_strategy: chunkStrategy,
      };
    }

    return {
      name: name.trim(),
      category,
      description: description.trim() || undefined,
    };
  };

  const handleSave = async (reprocessAfterSave: boolean) => {
    setSaving(true);
    setError(null);
    try {
      const updated = await updateBagelDocument(doc._id, buildPayload());
      onUpdated(updated);
      if (reprocessAfterSave) {
        await onReprocess(doc._id);
        openSuccessToaster(
          'Metadata saved and re-processing started',
          4000,
        );
      } else {
        openSuccessToaster(
          'Metadata saved — click Re-process to re-embed chunks',
          5000,
        );
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open onClose={onClose} size="sm">
      <Modal.Header>
        <Modal.Title>Edit Document Metadata</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Message type="error" showIcon style={{ marginBottom: 12 }}>
            {error}
          </Message>
        )}
        <Message type="info" showIcon style={{ marginBottom: 12 }}>
          Metadata changes do not update existing embeddings. Save, then
          re-process to regenerate chunks with the corrected context
          header.
        </Message>
        <Form fluid>
          <Form.Group>
            <Form.ControlLabel>Source file</Form.ControlLabel>
            <Input
              readOnly
              value={
                doc.originalFileName ||
                doc.metadata?.sourceFile ||
                doc.name
              }
            />
          </Form.Group>
          {isScla ? (
            <>
              <Form.Group>
                <Form.ControlLabel>Title *</Form.ControlLabel>
                <Input
                  value={title}
                  onChange={v => setTitle(String(v))}
                  placeholder="SCLA Fellowship Interview Guide 2016"
                />
              </Form.Group>
              <Form.Group>
                <Form.ControlLabel>Doc type</Form.ControlLabel>
                <Input
                  value={docType}
                  onChange={v => setDocType(String(v))}
                  placeholder="fellowship_screening"
                />
              </Form.Group>
              <Form.Group>
                <Form.ControlLabel>Tags</Form.ControlLabel>
                <Input
                  value={tags}
                  onChange={v => setTags(String(v))}
                  placeholder="interview, fellows, coaches, screening"
                />
              </Form.Group>
              <Form.Group>
                <Form.ControlLabel>Chunk strategy</Form.ControlLabel>
                <SelectPicker
                  data={CHUNK_STRATEGY_OPTIONS}
                  value={chunkStrategy}
                  onChange={v =>
                    setChunkStrategy(
                      (v || 'by_paragraph') as NonNullable<
                        BagelDocumentMetadata['chunkStrategy']
                      >,
                    )
                  }
                  cleanable={false}
                  block
                />
              </Form.Group>
              {doc.metadata?.year ? (
                <Form.Group>
                  <Form.ControlLabel>Year</Form.ControlLabel>
                  <Input readOnly value={doc.metadata.year} />
                </Form.Group>
              ) : null}
            </>
          ) : (
            <>
              <Form.Group>
                <Form.ControlLabel>Document name *</Form.ControlLabel>
                <Input value={name} onChange={v => setName(String(v))} />
              </Form.Group>
              <Form.Group>
                <Form.ControlLabel>Category *</Form.ControlLabel>
                <SelectPicker
                  data={CATEGORY_OPTIONS}
                  value={category}
                  onChange={v =>
                    setCategory(
                      (v as BagelDocumentCategory) || 'guidelines',
                    )
                  }
                  cleanable={false}
                  block
                />
              </Form.Group>
              <Form.Group>
                <Form.ControlLabel>Description</Form.ControlLabel>
                <AutoGrowTextarea
                  value={description}
                  onChange={v => setDescription(String(v))}
                  minRows={2}
                />
              </Form.Group>
            </>
          )}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={onClose} appearance="subtle" disabled={saving}>
          Cancel
        </Button>
        <Button
          appearance="default"
          onClick={() => handleSave(false)}
          disabled={saving || (isScla ? !title.trim() : !name.trim())}
          loading={saving}
        >
          Save
        </Button>
        <Button
          appearance="primary"
          onClick={() => handleSave(true)}
          disabled={saving || (isScla ? !title.trim() : !name.trim())}
          loading={saving}
        >
          Save &amp; Re-process
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
